import { GraphQLError, GraphQLErrorExtensions } from 'graphql'
import _ from 'lodash'
import { DUPLICATE_FILTER_ERROR_CODES } from './duplicate-filter.js'

enum ErrCode {
  // General error codes
  'unauthorized', // We don't know who you are
  'forbidden', // We know who you are, but you're not allowed to do that
  'not_found', // Resource not found
  'bad_request', // Request is malformed. For example, missing required fields
  'internal_server_error', // Something that should never happen happened
  'unknown_error', // Default error code
  // Feature specific error codes
  'bad_order', // attempt to insert an object (such as cell or row) at an invalid order
  'cell_update_failed', // failed to update a cell
  'chart_configuration_error', // error in chart configuration validation
  'chart_aggregation_error', // error in when trying to aggregate chart data
  'form_not_shareable', // an anonymous user attempted to load or submit a private form
  'form_schema_invalid', // submitted form data no longer matches the form schema
  'form_submission_invalid', // submitted values are invalid for the current form
}

// Converting the enum to a union of strings,
// so we get type safety without importing the enum all over the place
type ErrCodeParam =
  | keyof typeof ErrCode
  | (typeof DUPLICATE_FILTER_ERROR_CODES)[keyof typeof DUPLICATE_FILTER_ERROR_CODES]

/**
 * GraphQL has a `extensions.code` property that it writes,
 * so we're adding an additional errCode property,
 * so we can have a more meaningful error code,
 * but still see the error code that GraphQL returns,
 * which often is not useful, but sometimes provides a useful clue.
 */
interface DossGQLExtensions extends GraphQLErrorExtensions {
  errCode: ErrCodeParam
  /**
   * Whether this error was caused by the caller rather than by us: a request we were right to
   * refuse. A user error is not alerted on, because nothing is broken — the caller asked for
   * something they cannot have, sent a value we do not accept, or built a configuration we
   * cannot run.
   *
   * This is the only flag alerting reads. Default `false`, so an error is alerted on unless its
   * author decided otherwise. Judge it by the throw site, not by the class name: an error can
   * read like a rejection and still mean an invariant of ours was violated. `ChartAggregationError`
   * ("Aggregation is not the expected format for MEDIAN") is the example — no caller can provoke
   * it, so it stays `false`.
   */
  isUserError: boolean
  /**
   * Whether this error was unexpected. Read by the system-event categorizer and the workflow
   * interpreter, NOT by Sentry alerting — use `isUserError` for that.
   */
  isException: boolean
}

/** Base class for custom Doss errors */
export abstract class DossError extends GraphQLError {
  extensions: DossGQLExtensions

  constructor(message: string, extensions: Partial<DossGQLExtensions>) {
    super(message)
    // Provide default values and override with any extensions provided by subclasses
    this.extensions = {
      errCode: 'unknown_error',
      isUserError: false,
      isException: true,
      ...extensions,
    }
  }
}

export class UnauthorizedError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'unauthorized',
      isUserError: true,
      isException: false,
    })
  }
}

export class ForbiddenError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'forbidden',
      isUserError: true,
      isException: false,
    })
  }
}

export class NotFoundError extends DossError {
  constructor() {
    super('Resource not found', {
      errCode: 'not_found',
      isUserError: true,
      isException: false,
    })
  }
}

export class BadRequestError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'bad_request',
      isUserError: true,
      isException: false,
    })
  }
}

export class FormSchemaInvalidError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'form_schema_invalid',
      isUserError: true,
      isException: false,
    })
  }
}

export class FormNotShareableError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'form_not_shareable',
      isUserError: true,
      isException: false,
    })
  }
}

export class FormSubmissionInvalidError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'form_submission_invalid',
      isUserError: true,
      isException: false,
    })
  }
}

export class InternalServerError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'internal_server_error',
      isException: true,
    })
  }
}

export class DuplicateFilterTooManyValuesError extends DossError {
  constructor(columnName: string) {
    super(`Too many duplicate values in ${columnName} to filter — narrow the data`, {
      errCode: DUPLICATE_FILTER_ERROR_CODES.TOO_MANY_VALUES,
      isUserError: true,
      isException: false,
    })
  }
}

export class DuplicateFilterUnindexedValueError extends DossError {
  constructor(columnName: string) {
    super(`Some values in ${columnName} are too long for exact duplicate filtering`, {
      errCode: DUPLICATE_FILTER_ERROR_CODES.UNINDEXED_VALUE,
      isUserError: true,
      isException: false,
    })
  }
}

export class DuplicateFilterLimitExceededError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: DUPLICATE_FILTER_ERROR_CODES.LIMIT_EXCEEDED,
      isUserError: true,
      isException: false,
    })
  }
}

export class DuplicateFilterNotPermittedError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: DUPLICATE_FILTER_ERROR_CODES.NOT_PERMITTED,
      isUserError: true,
      isException: false,
    })
  }
}

/** Stable classification for a UDS query whose serialized rows exceed the server byte cap. */
export const UDS_RESULT_TOO_LARGE_ERROR_CODE = 'result_too_large'

/** One UDS SQL response may return at most 4 MiB of serialized rows. */
export const UDS_RESULT_LIMIT_BYTES = 4 * 1024 * 1024

export type UdsResultTooLargeDetails = {
  limitBytes: number
  resultBytes?: number
}

/** Actionable, row-count-independent guidance shared by GraphQL clients and the backend error. */
export function formatUdsResultTooLargeMessage({
  limitBytes,
  resultBytes,
}: UdsResultTooLargeDetails): string {
  const limitMiB = limitBytes / (1024 * 1024)
  const measured = resultBytes === undefined ? '' : ` (${String(resultBytes)} bytes measured)`
  return `Query result exceeded the ${String(limitMiB)} MiB limit${measured}. Narrow the projection, aggregate in SQL, or page with deterministic ordering.`
}

/** Backend error carrying only bounded result-size metadata, never query text or row data. */
export class UdsResultTooLargeError extends Error {
  readonly code = UDS_RESULT_TOO_LARGE_ERROR_CODE
  readonly limitBytes: number
  readonly resultBytes: number

  constructor(resultBytes: number, limitBytes = UDS_RESULT_LIMIT_BYTES) {
    super(formatUdsResultTooLargeMessage({ limitBytes, resultBytes }))
    this.name = 'UdsResultTooLargeError'
    this.limitBytes = limitBytes
    this.resultBytes = resultBytes
  }
}

export const UDS_ROW_LIMIT_TOO_LARGE_ERROR_CODE = 'row_limit_too_large'

export type UdsRowLimitParameter = 'maxRows' | 'page.limit'

export type UdsRowLimitTooLargeDetails = {
  parameter: UdsRowLimitParameter
  requestedRows: number
  maxRows: number
}

export const formatUdsRowLimitTooLargeMessage = ({
  parameter,
  requestedRows,
  maxRows,
}: UdsRowLimitTooLargeDetails): string =>
  `${parameter} requested ${String(requestedRows)} rows, exceeding the ${String(maxRows)}-row platform limit. Request smaller pages or use an ordered SQL LIMIT for intentional top-N results.`

/** Stable classification for a UDS result set whose driver-reported column names collide. */
export const UDS_DUPLICATE_RESULT_COLUMN_ERROR_CODE = 'duplicate_result_column'

/** User-fixable rejection: duplicate output names would silently shadow each other as row keys. */
export class UdsDuplicateResultColumnError extends Error {
  readonly code = UDS_DUPLICATE_RESULT_COLUMN_ERROR_CODE

  constructor(columnName: string) {
    super(`duplicate result column name "${columnName}"; disambiguate with AS`)
    this.name = 'UdsDuplicateResultColumnError'
  }
}

const graphqlExtensionsFrom = (candidate: unknown): Record<string, unknown> | undefined => {
  if (typeof candidate !== 'object' || candidate === null) {
    return undefined
  }
  const value = candidate as Record<string, unknown>
  return typeof value.extensions === 'object' && value.extensions !== null
    ? (value.extensions as Record<string, unknown>)
    : value
}

/**
 * Finds a stable extension code on Apollo v3, Apollo v4, a GraphQL error, or a domain error.
 * Callers receive extensions only; query variables and response data are never inspected.
 */
export function getGraphQLErrorExtensions(
  error: unknown,
  code: string,
): Record<string, unknown> | undefined {
  const direct = graphqlExtensionsFrom(error)
  if (direct?.code === code) {
    return direct
  }
  if (typeof error !== 'object' || error === null) {
    return undefined
  }
  const value = error as Record<string, unknown>
  for (const collection of [value.graphQLErrors, value.errors]) {
    if (!Array.isArray(collection)) {
      continue
    }
    for (const candidate of collection) {
      const extensions = graphqlExtensionsFrom(candidate)
      if (extensions?.code === code) {
        return extensions
      }
    }
  }
  return undefined
}

/** Reads bounded result-size metadata from the stable GraphQL/domain error classification. */
export function getUdsResultTooLargeDetails(error: unknown): UdsResultTooLargeDetails | undefined {
  const value = getGraphQLErrorExtensions(error, UDS_RESULT_TOO_LARGE_ERROR_CODE)
  if (value === undefined) {
    return undefined
  }
  const limitBytes =
    typeof value.limitBytes === 'number' &&
    Number.isSafeInteger(value.limitBytes) &&
    value.limitBytes > 0
      ? value.limitBytes
      : UDS_RESULT_LIMIT_BYTES
  const resultBytes =
    typeof value.resultBytes === 'number' &&
    Number.isSafeInteger(value.resultBytes) &&
    value.resultBytes >= 0
      ? value.resultBytes
      : undefined
  return { limitBytes, ...(resultBytes === undefined ? {} : { resultBytes }) }
}

/** User-facing result-limit guidance when the unknown error carries the stable classification. */
export function renderUdsResultTooLargeError(error: unknown): string | undefined {
  const details = getUdsResultTooLargeDetails(error)
  return details === undefined ? undefined : formatUdsResultTooLargeMessage(details)
}

export function getUdsRowLimitTooLargeDetails(
  error: unknown,
): UdsRowLimitTooLargeDetails | undefined {
  const value = getGraphQLErrorExtensions(error, UDS_ROW_LIMIT_TOO_LARGE_ERROR_CODE)
  if (value === undefined) {
    return undefined
  }
  const parameter = value.parameter
  const requestedRows = value.requestedRows
  const maxRows = value.maxRows
  if (
    (parameter !== 'maxRows' && parameter !== 'page.limit') ||
    typeof requestedRows !== 'number' ||
    !Number.isSafeInteger(requestedRows) ||
    requestedRows <= 0 ||
    typeof maxRows !== 'number' ||
    !Number.isSafeInteger(maxRows) ||
    maxRows <= 0
  ) {
    return undefined
  }
  return { parameter, requestedRows, maxRows }
}

export function renderUdsRowLimitError(error: unknown): string | undefined {
  const details = getUdsRowLimitTooLargeDetails(error)
  return details === undefined ? undefined : formatUdsRowLimitTooLargeMessage(details)
}

/** Bounded, allowlisted metadata for a failed UDS statement; never query text or row data. */
export type UdsSqlErrorMetadata = {
  code?: string
  limitBytes?: number
  resultBytes?: number
  parameter?: UdsRowLimitParameter
  requestedRows?: number
  maxRows?: number
}

/**
 * Stable UDS pagination errors. Cursor failures recover by restarting; the others require new SQL.
 */
export const UDS_PAGINATION_ERROR_CODES = [
  'cursor_invalid',
  'cursor_mismatch',
  'page_order_required',
  'page_shape_unsupported',
] as const

/**
 * The one classifier for UDS statement failures: maps an unknown error (domain error, GraphQL
 * error, or Apollo wrapper) onto its stable code and bounded metadata. Returns `{}` for
 * unclassified errors.
 */
export function getUdsSqlErrorMetadata(error: unknown): UdsSqlErrorMetadata {
  const tooLarge = getUdsResultTooLargeDetails(error)
  if (tooLarge !== undefined) {
    return {
      code: UDS_RESULT_TOO_LARGE_ERROR_CODE,
      limitBytes: tooLarge.limitBytes,
      ...(tooLarge.resultBytes === undefined ? {} : { resultBytes: tooLarge.resultBytes }),
    }
  }
  const oversizedRequest = getUdsRowLimitTooLargeDetails(error)
  if (oversizedRequest !== undefined) {
    return { code: UDS_ROW_LIMIT_TOO_LARGE_ERROR_CODE, ...oversizedRequest }
  }
  // The wasm loader attaches the translator's stable code to the thrown Error; GraphQL clients
  // see the same code through extensions. Only allowlisted codes cross, never arbitrary fields.
  for (const code of [
    ...UDS_PAGINATION_ERROR_CODES,
    UDS_ROW_LIMIT_TOO_LARGE_ERROR_CODE,
    UDS_DUPLICATE_RESULT_COLUMN_ERROR_CODE,
  ]) {
    const direct =
      typeof error === 'object' &&
      error !== null &&
      (error as Record<string, unknown>).code === code
    if (direct || getGraphQLErrorExtensions(error, code) !== undefined) {
      return { code }
    }
  }
  return {}
}

/**
 * Function to extract an error message from an Error object, string, or generic response object
 * handles runtime Error objects not being convertible to a JSON string
 */
export function getErrorMessageString(error: unknown): string {
  return formatErrorMessage(error, 0)
}

// Bounds `cause` recursion so a cyclic chain can't overflow the stack inside a catch block
const MAX_ERROR_CAUSE_DEPTH = 5

function formatErrorMessage(error: unknown, depth: number): string {
  if (_.isNil(error)) {
    return 'Unknown error (null/undefined)'
  }
  // Node's Fetch API/Undici's `TypeError: fetch failed` hides the real reason (ECONNREFUSED, DNS, TLS) in `cause`
  // https://github.com/nodejs/undici/issues/2990
  if (error instanceof TypeError) {
    const message = error.stack || error.message
    if (error.cause === undefined || depth >= MAX_ERROR_CAUSE_DEPTH) {
      return message
    }
    const causeMessage = formatErrorMessage(error.cause, depth + 1)
    // handling multiple error causes
    if (error.cause instanceof AggregateError) {
      const innerMessages = error.cause.errors.map(
        (inner: unknown, index) => `[errors[${index}]]: ${formatErrorMessage(inner, depth + 1)}`,
      )
      return [message, `Caused by: ${causeMessage}`, ...innerMessages].join('\n')
    }
    return `${message}\nCaused by: ${causeMessage}`
  }
  if (error instanceof Error) {
    // Optionally include stack trace for more detail
    return error.stack || error.message
  }
  if (typeof error === 'string') {
    return error
  }
  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}
